import { useState, useRef } from 'react';
import { CheckCircle, WarningCircle, Folder, Upload } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { uploadPhoto } from './PhotoCell';

/**
 * Bulk import photos by folder structure: Voluntario(V1)/Visita(T0)/foto.jpg
 * URS-F217. Assigns all photos to the same device passed in props
 * (since the importer button lives inside a specific device sonda).
 */
export function PhotoBulkImport({ studyId, device, volunteers, visits, onDone, lang, triggerLabel }) {
  const [open, setOpen] = useState(false);
  const [matched, setMatched] = useState([]);  // [{file, volunteer, visit, path}]
  const [errors, setErrors] = useState([]);    // [{path, reason}]
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ ok: 0, fail: 0, skipped: 0, total: 0 });
  const inputRef = useRef(null);

  const handleFolderPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Index by uppercase code for case-insensitive matching
    const volByCode = {};
    for (const v of volunteers) {
      if (v.study_volunteer_code) volByCode[v.study_volunteer_code.toUpperCase()] = v;
    }
    const visByCode = {};
    for (const v of visits) {
      // Visit objects use `code` (not `visit_code`) — see studies/{id}/visits endpoint
      const code = v.code || v.visit_code;
      if (code) visByCode[code.toUpperCase()] = v;
    }

    const okList = [];
    const errList = [];
    for (const f of files) {
      // webkitRelativePath e.g. "Estudio_XYZ/V1/T0/IMG_001.jpg"
      const parts = (f.webkitRelativePath || f.name).split('/').filter(Boolean);
      if (parts.length < 3) {
        errList.push({ path: f.webkitRelativePath || f.name, reason: lang === 'es' ? 'Estructura insuficiente (esperado Voluntario/Visita/archivo)' : 'Insufficient depth' });
        continue;
      }
      // Try the last 3 path segments first (root may be the study folder name)
      // [..., volunteer, visit, filename]
      const filename = parts[parts.length - 1];
      // Skip non-images
      if (!/\.(jpe?g|png|webp|heic|heif)$/i.test(filename)) {
        errList.push({ path: f.webkitRelativePath, reason: lang === 'es' ? 'No es imagen' : 'Not an image' });
        continue;
      }
      const visCandidate = parts[parts.length - 2].toUpperCase();
      const volCandidate = parts[parts.length - 3].toUpperCase();
      const vol = volByCode[volCandidate];
      const vis = visByCode[visCandidate];
      if (!vol) {
        errList.push({ path: f.webkitRelativePath, reason: lang === 'es' ? `Voluntario "${parts[parts.length - 3]}" no existe en el estudio` : `Volunteer not found` });
        continue;
      }
      if (!vis) {
        errList.push({ path: f.webkitRelativePath, reason: lang === 'es' ? `Visita "${parts[parts.length - 2]}" no existe en el estudio` : `Visit not found` });
        continue;
      }
      okList.push({
        file: f,
        path: f.webkitRelativePath,
        filename,
        volunteer_code: vol.study_volunteer_code,
        volunteer_name: vol.volunteer_name || '',
        visit_code: vis.code || vis.visit_code,
        visit_name: vis.name || '',
      });
    }

    setMatched(okList);
    setErrors(errList);
    setOpen(true);
  };

  const handleConfirm = async () => {
    if (matched.length === 0) return;
    setUploading(true);
    setProgress({ ok: 0, fail: 0, skipped: 0, total: matched.length });

    // Sequential to avoid VPS saturation; small parallelism via Promise.all in chunks of 3
    const chunkSize = 3;
    for (let i = 0; i < matched.length; i += chunkSize) {
      const chunk = matched.slice(i, i + chunkSize);
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(chunk.map(async (m) => {
        try {
          // URS-F299: el backend devuelve {skipped: true} si la foto ya existe
          const res = await uploadPhoto({
            studyId, device,
            volunteerCode: m.volunteer_code,
            visitCode: m.visit_code,
            file: m.file,
          });
          if (res && res.skipped) {
            setProgress(p => ({ ...p, skipped: p.skipped + 1 }));
          } else {
            setProgress(p => ({ ...p, ok: p.ok + 1 }));
          }
        } catch (err) {
          setProgress(p => ({ ...p, fail: p.fail + 1 }));
        }
      }));
    }

    setUploading(false);
    setProgress(p => {
      const parts = [];
      parts.push(`${p.ok} ${lang === 'es' ? 'nuevas' : 'new'}`);
      if (p.skipped) parts.push(`${p.skipped} ${lang === 'es' ? 'ya existian' : 'already imported'}`);
      if (p.fail) parts.push(`${p.fail} ${lang === 'es' ? 'fallos' : 'failed'}`);
      toast.success(`${matched.length} ${lang === 'es' ? 'fotos procesadas' : 'photos processed'}: ${parts.join(' · ')}`);
      return p;
    });
    if (onDone) onDone();
    setTimeout(() => {
      setOpen(false);
      setMatched([]);
      setErrors([]);
      setProgress({ ok: 0, fail: 0, skipped: 0, total: 0 });
    }, 1500);
  };

  // Group matched by volunteer/visit for the preview
  const grouped = matched.reduce((acc, m) => {
    const k = `${m.volunteer_code}__${m.visit_code}`;
    if (!acc[k]) acc[k] = { ...m, files: [] };
    acc[k].files.push(m.filename);
    return acc;
  }, {});

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        data-testid="photo-bulk-import-btn"
        onClick={() => inputRef.current?.click()}
        className="gap-2 border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50 rounded-sm"
      >
        <Folder size={14} />{triggerLabel || (lang === 'es' ? 'Importar carpeta' : 'Import folder')}
      </Button>
      <input
        ref={inputRef}
        type="file"
        // @ts-ignore - non-standard but widely supported
        webkitdirectory="true"
        directory="true"
        multiple
        className="hidden"
        onChange={handleFolderPick}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload size={18} className="text-fuchsia-600" />
              {lang === 'es' ? 'Importar fotos desde carpeta' : 'Import photos from folder'}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {lang === 'es' ? 'Dispositivo destino:' : 'Target device:'} <b className="text-stone-700">{device}</b>
              <span className="mx-2">·</span>
              {lang === 'es' ? 'Estructura esperada: <code>Voluntario/Visita/archivo.jpg</code> (ej. V1/T0/foto.jpg)' : 'Expected: Volunteer/Visit/file.jpg'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 flex items-center gap-2">
                <CheckCircle size={20} className="text-emerald-600" />
                <div>
                  <div className="text-xs text-emerald-700">{lang === 'es' ? 'Reconocidos' : 'Recognized'}</div>
                  <div className="text-xl font-bold text-emerald-900" data-testid="bulk-import-matched-count">{matched.length}</div>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-center gap-2">
                <WarningCircle size={20} className="text-amber-600" />
                <div>
                  <div className="text-xs text-amber-700">{lang === 'es' ? 'Sin match' : 'No match'}</div>
                  <div className="text-xl font-bold text-amber-900" data-testid="bulk-import-errors-count">{errors.length}</div>
                </div>
              </div>
            </div>

            {Object.keys(grouped).length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">{lang === 'es' ? 'Vista previa' : 'Preview'}</h4>
                <div className="bg-white border border-stone-200 rounded-sm divide-y divide-stone-100 max-h-[40vh] overflow-y-auto">
                  {Object.values(grouped).map((g) => (
                    <div key={`${g.volunteer_code}-${g.visit_code}`} className="p-2.5 text-xs">
                      <div className="font-semibold text-stone-800">
                        {g.volunteer_code} / {g.visit_code}
                        <span className="text-stone-400 font-normal ml-1">→ {g.volunteer_name} · {g.visit_name}</span>
                        <span className="text-fuchsia-600 ml-2 font-mono">[{g.files.length}]</span>
                      </div>
                      <div className="pl-3 text-stone-500 mt-0.5">
                        {g.files.slice(0, 5).join(' · ')}
                        {g.files.length > 5 ? ` (+${g.files.length - 5} más)` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {errors.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1.5">{lang === 'es' ? 'Sin match (no se subiran)' : 'No match (will be skipped)'}</h4>
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-2 max-h-[20vh] overflow-y-auto space-y-0.5">
                  {errors.slice(0, 30).map((e, i) => (
                    <div key={i} className="text-[11px] text-amber-800 font-mono">
                      <span className="text-stone-600">{e.path}</span> — <span className="italic">{e.reason}</span>
                    </div>
                  ))}
                  {errors.length > 30 && <div className="text-[11px] text-amber-700 italic">... y {errors.length - 30} más</div>}
                </div>
              </div>
            )}

            {uploading && (
              <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-sm p-3">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-semibold text-fuchsia-800">{lang === 'es' ? 'Subiendo...' : 'Uploading...'}</span>
                  <span className="font-mono text-fuchsia-700" data-testid="bulk-import-progress-counter">{progress.ok + progress.fail + progress.skipped} / {progress.total}</span>
                </div>
                <div className="w-full bg-fuchsia-100 rounded-full h-2 overflow-hidden">
                  <div className="bg-fuchsia-600 h-full transition-all" style={{ width: `${((progress.ok + progress.fail + progress.skipped) / Math.max(1, progress.total)) * 100}%` }} />
                </div>
                <div className="flex gap-3 text-[11px] mt-1">
                  {progress.ok > 0 && <span className="text-emerald-700">{progress.ok} {lang === 'es' ? 'nuevas' : 'new'}</span>}
                  {progress.skipped > 0 && <span className="text-amber-700" data-testid="bulk-import-skipped-counter">{progress.skipped} {lang === 'es' ? 'ya existian' : 'already imported'}</span>}
                  {progress.fail > 0 && <span className="text-red-600">{progress.fail} {lang === 'es' ? 'fallaron' : 'failed'}</span>}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={uploading}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
            <Button
              data-testid="bulk-import-confirm-btn"
              onClick={handleConfirm}
              disabled={matched.length === 0 || uploading}
              className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
            >
              {uploading ? '...' : (lang === 'es' ? `Subir ${matched.length} fotos` : `Upload ${matched.length} photos`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
